import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate a session ID once per tab
const SESSION_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Page name mapping
const PAGE_NAMES = {
  '/': 'Accueil',
  '/marche': 'Marche & Opportunite',
  '/vision': 'Vision & Objectifs',
  '/produit': 'Produit & Experience',
  '/propriete-intellectuelle': 'Propriete Intellectuelle',
  '/paiements': 'Paiements & Securite',
  '/plan-execution': "Plan d'Execution",
  '/go-to-market': 'Go-to-Market',
  '/roadmap': 'Roadmap & Jalons',
  '/resume-executif': 'Resume Executif',
  '/modele-affaires': "Modele d'Affaires",
  '/organisation': 'Organisation & Couts',
  '/valorisation': 'Valorisation',
  '/levee-fonds': 'Levee de Fonds',
  '/synthese-investisseur': 'Synthese Investisseur',
  '/previsions': 'Previsions Financieres',
  '/invest-deck': 'Invest Deck',
  '/ronde-seed': 'Ronde Seed',
  '/print-all': 'Export PDF',
};

function getPageName(pathname) {
  return PAGE_NAMES[pathname] || pathname;
}

function sendEvent(data) {
  try {
    const payload = JSON.stringify({
      ...data,
      session_id: SESSION_ID,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      referrer: document.referrer || '',
    });
    // Use sendBeacon for reliability on page leave, fetch otherwise
    if (data.event_type === 'page_leave' && navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/api/tracking/event`, new Blob([payload], { type: 'application/json' }));
    } else {
      fetch(`${API}/api/tracking/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) {}
}

export function useTracking() {
  const location = useLocation();
  const startTime = useRef(Date.now());
  const maxScroll = useRef(0);
  const sectionsReached = useRef(new Set());
  const currentPage = useRef('');

  const trackScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const pct = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
    if (pct > maxScroll.current) maxScroll.current = pct;

    // Detect visible sections
    const sections = document.querySelectorAll('section[id]');
    sections.forEach((sec) => {
      const rect = sec.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        sectionsReached.current.add(sec.id);
      }
    });
  }, []);

  const sendLeave = useCallback(() => {
    const duration = Math.round((Date.now() - startTime.current) / 1000);
    if (currentPage.current) {
      sendEvent({
        page: currentPage.current,
        event_type: 'page_leave',
        duration,
        scroll_depth: maxScroll.current,
        sections_reached: [...sectionsReached.current],
      });
    }
  }, []);

  useEffect(() => {
    // Skip tracking for admin page
    if (location.pathname === '/admin/analytics') return;

    // Send leave for previous page
    sendLeave();

    // Reset for new page
    const pageName = getPageName(location.pathname);
    currentPage.current = pageName;
    startTime.current = Date.now();
    maxScroll.current = 0;
    sectionsReached.current = new Set();

    // Send page view
    sendEvent({ page: pageName, event_type: 'page_view', duration: 0, scroll_depth: 0, sections_reached: [] });

    // Scroll listener
    window.addEventListener('scroll', trackScroll, { passive: true });

    // Heartbeat every 30s
    const heartbeat = setInterval(() => {
      const dur = Math.round((Date.now() - startTime.current) / 1000);
      sendEvent({
        page: currentPage.current,
        event_type: 'heartbeat',
        duration: dur,
        scroll_depth: maxScroll.current,
        sections_reached: [...sectionsReached.current],
      });
    }, 30000);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', trackScroll);
      clearInterval(heartbeat);
    };
  }, [location.pathname, trackScroll, sendLeave]);

  // Track page leave on tab close
  useEffect(() => {
    const handleUnload = () => sendLeave();
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [sendLeave]);
}
